import { useEffect, useMemo, useState } from "react"
import { usePage } from "@inertiajs/react"
import { StaffLayout } from "@/components/layouts/staff-layout"
import { MainContainer } from "@/components/ui/container"
import { DataTable } from "@/components/ui/data-table"
import type { AppliedFilters, AppliedSort, DataColumn } from "@/components/ui/data-table"
import { PageHeader } from "@/components/ui/headers"
import { Button } from "@/components/ui/button"
import { StatusBadge } from "@/components/ui/status-badge"
import { billTone } from "@/lib/status-colors"
import { BillDetailDrawer, type StaffBillRow } from "@/features/staff/bill-detail-drawer"
import type { CfdiSettings } from "@/features/staff/invoices/invoices"
import { formatDateTime, formatMoney } from "@/lib/formatters"
import { t } from "@/lib/i18n"

type Pagination = {
  page: number
  perPage: number
  totalCount: number
  totalPages: number
  hasPrevious: boolean
  hasNext: boolean
  previousPage: number | null
  nextPage: number | null
}

type Props = {
  bills: StaffBillRow[]
  pagination: Pagination
  search: string
  sort: AppliedSort
  filters: AppliedFilters
  /**
   * null when the restaurant does not have `cfdi_invoicing` switched on. The
   * server decides; this page never re-derives it from a feature list.
   */
  cfdi: CfdiSettings | null
  /** The restaurant's IANA zone (react.md rule 16). */
  timeZone: string
}

const TABLE_PROPS = [ "bills", "pagination", "search", "sort", "filters" ]

export default function StaffLocationBillsIndex() {
  const { bills, pagination, search, sort, filters, cfdi, timeZone } = usePage<Props>().props
  const [ selectedBillId, setSelectedBillId ] = useState<number | null>(null)
  const [ drawerOpen, setDrawerOpen ] = useState(false)

  useEffect(() => {
    if (selectedBillId === null) return
    if (bills.some((bill) => bill.id === selectedBillId)) return

    setSelectedBillId(null)
    setDrawerOpen(false)
  }, [ bills, selectedBillId ])

  const selectedBill = bills.find((bill) => bill.id === selectedBillId) ?? null

  const openBillDrawer = (billId: number) => {
    setSelectedBillId(billId)
    setDrawerOpen(true)
  }

  const columns = useMemo<DataColumn<StaffBillRow>[]>(
    () => [
      {
        accessorKey: "id",
        header: t("ui.react.pages.staff.location.bills.index.bill"),
        // The field declares what this column *is* — the row's identity, so the
        // cell renders as a `th scope="row"` — while the `cell` below decides
        // how it looks. The two are independent, which is what lets a row whose
        // identity opens a drawer still be announced as that row's header.
        field: { type: "title", mono: true },
        sortKey: "id",
        cell: ({ row }) => (
          <Button
            type="button"
            variant="link"
            className="px-0 font-mono text-sm"
            onClick={() => openBillDrawer(row.original.id)}
            data-testid={`bill-row-button-${row.original.id}`}
          >
            #{row.original.id}
          </Button>
        ),
      },
      {
        accessorKey: "status",
        header: "Estado",
        sortKey: "status",
        cell: ({ row }) => (
          <StatusBadge tone={billTone(row.original.status)} label={row.original.statusLabel} />
        ),
      },
      {
        accessorKey: "tableNames",
        header: "Mesas",
        cell: ({ row }) =>
          row.original.tableNames.length > 0
            ? row.original.tableNames.join(", ")
            : t("ui.common.no_table"),
      },
      {
        accessorKey: "ordersCount",
        header: t("ui.common.order", { count: 2 }),
        field: { type: "number" },
      },
      {
        accessorKey: "unpaidOrdersCount",
        header: t("enums.location.order.payment_status.unpaid"),
        field: { type: "number" },
      },
      {
        accessorKey: "total",
        header: "Total",
        field: { type: "currency" },
        sortKey: "total",
      },
      {
        accessorKey: "pendingPaymentAttempt",
        header: "Cobro",
        cell: ({ row }) => (row.original.pendingPaymentAttempt ? "En curso" : "—"),
      },
      {
        accessorKey: "updatedAt",
        header: "Actualizada",
        field: { type: "datetime" },
        sortKey: "updated_at",
      },
    ],
    []
  )

  return (
    <StaffLayout>
      <MainContainer size="md">
        <div className="space-y-6" data-testid="bills-page">
          <PageHeader
            title={t("ui.react.pages.staff.location.bills.index.bills")}
            description={`Mostrando ${bills.length} de ${pagination.totalCount} cuentas de la ubicación`}
          />

          <DataTable
            mode="server"
            url="/bills"
            columns={columns}
            data={bills}
            surface="card"
            search={search}
            sort={sort}
            filters={filters}
            pagination={pagination}
            reloadOnly={TABLE_PROPS}
            timeZone={timeZone}
            searchPlaceholder={t("ui.react.pages.staff.location.bills.index.search_bill_status")}
            // Hand-written because the whole card opens the drawer, which the
            // generated feed has no way to express.
            renderMobileCard={(bill) => (
              <button
                type="button"
                onClick={() => openBillDrawer(bill.id)}
                className="hover:bg-muted/50 w-full px-4 py-3 text-left transition-colors"
                data-testid={`bill-card-${bill.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-sm font-medium">#{bill.id}</span>
                  <StatusBadge tone={billTone(bill.status)} label={bill.statusLabel} />
                </div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <span className="text-muted-foreground min-w-0 truncate text-sm">
                    {bill.tableNames.length > 0
                      ? bill.tableNames.join(", ")
                      : t("ui.common.no_table")}{" "}
                    · {t("ui.common.order", { count: 2 })}: {bill.ordersCount}
                    {bill.pendingPaymentAttempt ? " · Cobro: En curso" : ""}
                  </span>
                  <span className="shrink-0 text-sm font-semibold tabular-nums">
                    {formatMoney(bill.total)}
                  </span>
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  {formatDateTime(bill.updatedAt, { timeZone })}
                </p>
              </button>
            )}
          />
        </div>
      </MainContainer>

      <BillDetailDrawer
        bill={selectedBill}
        cfdi={cfdi}
        timeZone={timeZone}
        open={drawerOpen}
        onOpenChange={(open) => {
          setDrawerOpen(open)
          if (!open) setSelectedBillId(null)
        }}
      />
    </StaffLayout>
  )
}
